import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { Controller } from 'react-hook-form';
import { useMemo } from 'react';
import { TextFieldProps } from '@mui/material';

import { AlxTextField, themeColors } from '../../alx-components/input';
import { isOverlappedByHeaderOrNavBtns } from '../../../utils/ux.util';
import { RootState } from '../../../store/rootReducer';
import { UseControllerProps } from './forms-types';

type FilterType = 'all' | 'alphanumeric' | 'numeric' | 'alphabetic' | 'phone';

type FilterFn = RegExp | ((value: string) => string);

const FILTER_REGEX: Record<Exclude<FilterType, 'all'>, FilterFn> = {
  alphanumeric: /[^a-zA-Z0-9]/g,
  numeric: /[^0-9]/g,
  alphabetic: /[^a-zA-Z]/g,
  phone: (value: string) => {
    const hasPlus = value.startsWith('+');
    const digits = value.replace(/[^0-9]/g, '');
    return hasPlus ? `+${digits}` : digits;
  },
};

type inputProps = TextFieldProps & {
  'data-cy'?: string; // 👈 Cypress selector
  formatOnChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => string;
  filter?: FilterType;
};

interface InputControllerProps {
  inputProps?: inputProps;
  controllerProps: UseControllerProps;
}

const AlxTextFieldToUse = styled(AlxTextField)`
  .MuiFilledInput-root:hover {
    border: 1px solid var(--color);
  }
  .MuiFormLabel-root.Mui-focused {
    color: var(--focus);
  }
`;

const ControlledInput: React.FC<InputControllerProps> = ({ inputProps, controllerProps }) => {
  const { formatOnChange, filter, ...restOfInputProps } = inputProps || {};

  const providerCode = useSelector((state: RootState) => state.general.provider);

  const themeInputs = useMemo(() => {
    if (providerCode) {
      return themeColors[providerCode as keyof typeof themeColors];
    }
    return themeColors['00000016'];
  }, [providerCode]);

  return (
    <Controller
      {...controllerProps}
      render={({ field }) => (
        <AlxTextFieldToUse
          style={themeInputs}
          {...field}
          inputRef={field.ref}
          {...restOfInputProps}
          autoComplete="off"
          onChange={(e) => {
            const hasTransform = formatOnChange || (filter && filter !== 'all');
            if (hasTransform) {
              let value = formatOnChange ? formatOnChange(e) : e.target.value;
              if (filter && filter !== 'all') {
                const filterFn = FILTER_REGEX[filter];
                value = typeof filterFn === 'function' ? filterFn(value) : value.replace(filterFn, '');
              }
              field.onChange(value);
              return;
            }
            field.onChange(e);
          }}
          onFocus={(event) => {
            isOverlappedByHeaderOrNavBtns(event.target);
          }}
        />
      )}
      rules={{
        ...controllerProps.rules,
      }}
    />
  );
};

export default ControlledInput;
